import { ValueOf } from "./utility";
import { WiseOwlIngress } from "./wiseOwlApi";

export const SurveyVariantEnum = {
  AGENT: "AGENT",
  HELP_CENTER_ASSISTANT: "HCA",
} as const;

export type SurveyVariantEnumType = ValueOf<typeof SurveyVariantEnum>;

export interface EmbeddedData {
  chatId: string;
  chatTimestamp: string;
  firstName?: string;
  ingress?: string;
  lastName?: string;
  liveChatId?: string;
  recipientEmail?: string;
  sendGridUserId?: string;
  supportProduct?: string;
  surveyVariant?: string;
  ticketId?: number;
}

// https://api.qualtrics.com/26ae1e3a11f83-working-with-surveys-using-the-conversational-ap-is#multiple-choice-response-types-mc
export type MultipleChoiceResponse = Record<
  string,
  {
    selected: boolean;
    text?: string;
  }
>;

// https://api.qualtrics.com/26ae1e3a11f83-working-with-surveys-using-the-conversational-ap-is#multiple-choice-fuzzy-response
export interface MultipleChoiceFuzzyResponse {
  fuzzySearch: string;
  maxMatches: number;
}

// https://api.qualtrics.com/26ae1e3a11f83-working-with-surveys-using-the-conversational-ap-is#text-entry-response
// Explicitly calling out that TextEntryResponse is a string to help with the data mental model.
export type TextEntryResponse = string;

// https://api.qualtrics.com/26ae1e3a11f83-working-with-surveys-using-the-conversational-ap-is#updating-a-survey-session
// https://api.qualtrics.com/26ae1e3a11f83-working-with-surveys-using-the-conversational-ap-is#response-types
export type SurveyResponse = Record<
  string,
  MultipleChoiceResponse | MultipleChoiceFuzzyResponse | TextEntryResponse
>;

export interface SurveySessionMeta {
  requestId: string;
  httpStatus: string;
}

export interface SessionResponse<T> {
  result: T;
  meta: SurveySessionMeta;
}

export const QuestionTypeEnum = {
  MultipleChoice: "mc",
  TextEntry: "te",
  DescriptiveBlock: "db",
} as const;

export interface Choices {
  choiceId: string;
  display: string;
  textual: boolean;
}

export interface MultipleChoiceOptions {
  columnLabels: string[];
  multiSelect: boolean;
}

export interface MultipleChoiceQuestion {
  questionId: string;
  type: typeof QuestionTypeEnum.MultipleChoice;
  display: string;
  options: MultipleChoiceOptions;
  choices: Choices[];
}

export interface TextEntryQuestion {
  questionId: string;
  type: typeof QuestionTypeEnum.TextEntry;
  display: string;
}

export interface DescriptiveBlock {
  questionId: string;
  type: typeof QuestionTypeEnum.DescriptiveBlock;
  display: string;
}

export type SurveyQuestion =
  | MultipleChoiceQuestion
  | TextEntryQuestion
  | DescriptiveBlock;

export interface BaseSessionResult {
  sessionId: string;
  questions: SurveyQuestion[];
  responses: SurveyResponse;
}

export interface CreateSessionEmbeddedData {
  surveyVariant?: SurveyVariantEnumType;
  agentName?: string;
}

export interface UpdateSessionEmbeddedData {
  accountSid?: string;
  chatId: string;
  chatTimestamp: string;
  firstName?: string;
  ingress?: string;
  lastName?: string;
  liveChatId?: string;
  recipientEmail?: string;
  supportProduct?: string;
  surveyVariant?: string;
  ticketId?: number;
  userId?: string;
}

export interface CreateSessionResult extends BaseSessionResult {
  embeddedData?: CreateSessionEmbeddedData;
  done: boolean;
}

export interface UpdateSessionResult extends BaseSessionResult {
  embeddedData: UpdateSessionEmbeddedData;
  done: string;
}

export type CreateSurveySessionResponse = SessionResponse<CreateSessionResult>;

export type UpdateSurveySessionResponse = SessionResponse<UpdateSessionResult>;

// Using null vs optional params because that's what Prisma returns.
export interface SurveyResponseReceipt {
  id: string;
  accountSid: string | null;
  createdAt: Date;
  updatedAt: Date;
  surveyId: string;
  surveyVariant: string | null;
  supportProduct: string | null;
  chatId: string;
  liveChatId: string | null;
  ticketId: string | null;
  userId: string | null;
  sendGridUserId: string | null;
}

export interface GetSurveyResponseReceipt {
  responseReceipts: SurveyResponseReceipt[];
}

export interface SurveySessionCreateArgs {
  surveyId: string;
  surveyVariant: SurveyVariantEnumType;
  headers?: never;
}

export interface SurveySessionUpdateArgs {
  surveyId: string;
  sessionId: string;
  embeddedData: EmbeddedData;
  responses: SurveyResponse;
  sendGridUserId?: string;
}
